import {ChangeEvent, ReactElement, useState} from 'react'

import {FormControl, TextField, Button, MenuItem, InputLabel, Select} from '@material-ui/core'
import {Trans} from 'react-i18next'
import {PixAccountTypes, clientTypes} from '@bdswiss/common-enums'

import {ClientType, Maybe} from '../../../../../../../graphql/types'
import messages from '../../../../../../../assets/messages'
import {PIX_PAYOUT_FIELD_NAMES, PIX_PAYOUT_DEFAULT_FIELD_VALUES} from '../../../constants'
import {useStyles} from './styles'

type PayRetailerFieldsProps = {
  paymentFields: Record<string, any> | null
  onPaymentMethodSet: (fieldValues) => void
  clientType: Maybe<ClientType>
  onClose: () => void
}

const pixAccountTypesOptions = Object.values(PixAccountTypes)

const corporateClientTypes = Object.freeze([
  clientTypes.ibCorporate.value,
  clientTypes.affiliateCorporate.value,
]) as ClientType[]

const digitsOnlyRegex = /^\d+$/

function checkIsCustomerIdentityValid(value: string, expectedDigits: number): boolean {
  return value.length === 0 || (digitsOnlyRegex.test(value) && value.length === expectedDigits)
}

export function PixPayoutFields(props: PayRetailerFieldsProps): ReactElement {
  const classes = useStyles()
  const {paymentFields, onPaymentMethodSet, onClose, clientType} = props
  const [formFieldsValues, setFormFieldsValues] = useState(
    paymentFields || PIX_PAYOUT_DEFAULT_FIELD_VALUES,
  )

  // Corporate PIX activation
  let corporateClientDetected = clientType && corporateClientTypes.includes(clientType)
  corporateClientDetected = false

  const fieldsVisibilityMap = {
    [PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity]: true,
    [PIX_PAYOUT_FIELD_NAMES.pixAccountType]: true,
    [PIX_PAYOUT_FIELD_NAMES.pixAccountId]:
      formFieldsValues.pixAccountType &&
      ![PixAccountTypes.cpf.value, PixAccountTypes.cnpj.value].includes(
        formFieldsValues.pixAccountType,
      ),
  }
  const isEmptyFieldPresentInTheForm = Object.keys(formFieldsValues).some((key) => {
    const isVisible = fieldsVisibilityMap[key]
    const value = formFieldsValues[key]
    return isVisible && !value.toString().trim()
  })

  const expectedCustomerIdentityLength = corporateClientDetected ? 14 : 11
  const isCustomerIdentityValid = checkIsCustomerIdentityValid(
    formFieldsValues.pixCustomerIdentity,
    expectedCustomerIdentityLength,
  )

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {value, name} = event.target
    setFormFieldsValues((prevValues) => ({...prevValues, [name]: value}))
  }

  const handleSelectFieldChange = (
    event: ChangeEvent<{name?: string | undefined; value: unknown}>,
  ) => {
    const {value, name} = event.target
    if (!name || !value) return

    setFormFieldsValues((prevValues) => ({...prevValues, [name]: value}))
  }

  const handleSave = () => {
    onPaymentMethodSet(formFieldsValues)
    onClose()
  }
  const pixAccountTypes = pixAccountTypesOptions.filter((opt) => {
    if (corporateClientDetected) return opt.value !== PixAccountTypes.cpf.value
    return opt.value !== PixAccountTypes.cnpj.value
  })
  const pixAccountIdLabelsMap = {
    [PixAccountTypes.cpf.value]: <Trans {...messages.personalIdentificationNumber} />,
    [PixAccountTypes.email.value]: <Trans {...messages.email} />,
    [PixAccountTypes.phone.value]: <Trans {...messages.phoneNumber} />,
    [PixAccountTypes.evp.value]: <Trans {...messages.evp} />,
  }

  const customerIdentityLabel = corporateClientDetected
    ? messages.cnpjNumber
    : messages.personalIdentificationNumber
  const customerIdentityLengthWarning = (
    <Trans {...messages.mustBeNDigits} values={{numDigits: expectedCustomerIdentityLength}} />
  )

  const isSubmitDisabled = isEmptyFieldPresentInTheForm || !isCustomerIdentityValid

  return (
    <>
      <FormControl fullWidth>
        <InputLabel>
          <Trans {...messages.pixAccountType} />
        </InputLabel>
        <Select
          name={PIX_PAYOUT_FIELD_NAMES.pixAccountType}
          value={formFieldsValues.pixAccountType}
          onChange={handleSelectFieldChange}
        >
          {pixAccountTypes.map(({value, label}) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <TextField
          required
          type="text"
          name={PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity}
          value={formFieldsValues.pixCustomerIdentity}
          label={<Trans {...customerIdentityLabel} />}
          onChange={handleTextFieldChange}
          error={!isCustomerIdentityValid}
          helperText={!isCustomerIdentityValid ? customerIdentityLengthWarning : undefined}
        />
      </FormControl>
      {fieldsVisibilityMap['pixAccountId'] ? (
        <FormControl fullWidth>
          <TextField
            required
            type="text"
            name={PIX_PAYOUT_FIELD_NAMES.pixAccountId}
            value={formFieldsValues.pixAccountId}
            label={pixAccountIdLabelsMap[formFieldsValues.pixAccountType]}
            onChange={handleTextFieldChange}
          />
        </FormControl>
      ) : null}
      <Button
        disabled={isSubmitDisabled}
        variant="contained"
        color="primary"
        onClick={handleSave}
        className={classes.button}
      >
        <Trans {...messages.saveAndContinue} />
      </Button>
    </>
  )
}
